import type { AppointmentWithDisplay, NavCounts } from '@ergo/shared';
import { type ReactNode, createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { api } from './api';
import { useResource } from './hooks';

/** Rien à faire : la barre ne montre alors aucune pastille. */
const NOTHING_PENDING: NavCounts = { pointage: 0, notes: 0, debtors: 0, quarterPending: false };

interface UnloggedValue {
  items: AppointmentWithDisplay[];
  loading: boolean;
  error: string | null;
  /** Call after logging an outcome anywhere in the app. */
  refresh: () => void;
}

const UnloggedContext = createContext<UnloggedValue | null>(null);
const NavCountsContext = createContext<NavCounts>(NOTHING_PENDING);
const RefreshContext = createContext<() => void>(() => {});

/**
 * Ce que la barre de navigation attend, et la liste du Pointage avec — une seule
 * source, rafraîchie d'un geste. Les trois pastilles viennent du serveur : pointer
 * une séance, émettre ou envoyer une note, saisir un paiement les déplacent toutes.
 */
export function UnloggedProvider({ children }: { children: ReactNode }) {
  const unlogged = useResource(() => api.appointments.unlogged(), []);
  const counts = useResource(() => api.navCounts(), []);

  const refresh = useCallback(() => {
    unlogged.reload();
    counts.reload();
  }, [unlogged.reload, counts.reload]);

  // Séances become due while the app sits in the background on her phone.
  useEffect(() => {
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [refresh]);

  const value = useMemo<UnloggedValue>(
    () => ({
      items: unlogged.data ?? [],
      loading: unlogged.loading,
      error: unlogged.error,
      refresh,
    }),
    [unlogged.data, unlogged.loading, unlogged.error, refresh],
  );

  return (
    <RefreshContext.Provider value={refresh}>
      <NavCountsContext.Provider value={counts.data ?? NOTHING_PENDING}>
        <UnloggedContext.Provider value={value}>{children}</UnloggedContext.Provider>
      </NavCountsContext.Provider>
    </RefreshContext.Provider>
  );
}

export function useUnlogged(): UnloggedValue {
  const context = useContext(UnloggedContext);
  if (!context) throw new Error('useUnlogged must be used inside UnloggedProvider');
  return context;
}

/** Les nombres des pastilles ; zéro partout tant que rien n'est chargé. */
export function useNavCounts(): NavCounts {
  return useContext(NavCountsContext);
}

/**
 * À appeler après tout ce qui déplace une pastille : un pointage, une note émise ou
 * envoyée, un paiement saisi ou supprimé.
 */
export function useRefreshNavCounts(): () => void {
  return useContext(RefreshContext);
}
